// Applies an airline brand (or the Vamos default) to the whole document: CSS colour variables,
// favicon. Used by the staff sign-in pages (BrandProvider), the signed-in app (AppContent) and
// before the first paint (index.js) so a remembered brand never flashes Vamos colours first.
import { resetCompanyStyle, setCompanyStyle } from '../util/theme'
import { DEFAULT_FAVICON } from './brandDefaults'

// Tab / installed-app window title. The env name carries the environment suffix
// ("Vamos App (Local)", "Vamos App (Dev)"); airline titles keep the same suffix.
export const DEFAULT_TITLE = process.env.REACT_APP_APP_NAME || 'Vamos App'
const ENV_SUFFIX = DEFAULT_TITLE.replace(/^Vamos App/, '')

/** "Kenya Airways Staff (Local)" — the signed-in app title, matching the airline PWA's name. */
export function brandAppTitle(brand) {
  return brand?.name ? `${brand.name} Staff${ENV_SUFFIX}` : DEFAULT_TITLE
}

/**
 * True inside the installed app (PWA) window. Its title bar already shows the app's name
 * ("Air Transat Staff"), and Chrome adds the page title after it, so titles there leave out the
 * airline/app name instead of repeating it ("Air Transat Staff - Air Transat · Staff sign in").
 */
export function isInstalledApp(win = typeof window !== 'undefined' ? window : undefined) {
  try {
    return !!(
      win &&
      ((win.matchMedia && win.matchMedia('(display-mode: standalone)').matches) ||
        win.navigator?.standalone === true)
    )
  } catch (e) {
    return false
  }
}

/** "{Airline} · {page}" in a browser tab; just "{page}" in the installed app window. */
export function pageTitleFor(brandName, pageTitle) {
  if (isInstalledApp() || !brandName) return pageTitle || ''
  return pageTitle ? `${brandName} · ${pageTitle}` : brandName
}

function setTitle(title) {
  if (typeof document !== 'undefined' && document.title !== title) document.title = title
}

// Safari ignores SVG favicons; only swap to raster icons.
const RASTER_ICON_RE = /\.(png|ico)(\?.*)?$/i

function setFavicon(href) {
  if (typeof document === 'undefined') return
  let link = document.head.querySelector('link[rel="icon"], link[rel="shortcut icon"]')
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'icon')
    document.head.appendChild(link)
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href)
}

/** `brand` is a company-like object { code, name, logo, icon, color1, color2 } or null (Vamos). */
export function applyBrand(brand) {
  if (!brand) {
    resetBrand()
    return
  }
  resetCompanyStyle()
  setCompanyStyle(brand)
  setFavicon(faviconFor(brand))
}

function faviconFor(brand) {
  return brand?.icon && RASTER_ICON_RE.test(brand.icon) ? brand.icon : DEFAULT_FAVICON
}

/** Signed-in app: the airline staff app's title (Vamos staff and developers: the default). */
export function applyAppTitle(brand) {
  // In the installed app the window already shows the app's name; an empty title avoids
  // "Air Transat Staff - Air Transat Staff".
  setTitle(isInstalledApp() ? '' : brandAppTitle(brand))
}

/**
 * Passenger pages (voucher, wallet, print, consent, ...): the airline's favicon and
 * "{Airline} · {page}" in the tab. Colours are set by the page itself (setCompanyStyle).
 */
export function brandPublicPage(company, pageTitle) {
  if (!company) return
  setFavicon(faviconFor(company))
  if (company.name) setTitle(pageTitleFor(company.name, pageTitle))
}

export function resetBrand() {
  resetCompanyStyle()
  setFavicon(DEFAULT_FAVICON)
  setTitle(DEFAULT_TITLE)
}
